import DrawingCanvas from '../../pain-diagram/DrawingCanvas';
import PainLocationChecklist from './PainLocationChecklist';
import FibromyalgiaTenderPoints from './FibromyalgiaTenderPoints';
import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import logger from '../../utils/logger';
import { secureSessionStorage, secureLocalStorage } from '../../utils/secure-session';

const Step5Diagram = forwardRef(({ onSubmitWithPopup, canvasGender, setCanvasGender, previousDrawings }, ref) => {
  const fileInputRef = useRef(null);
  const [canvasRef, setCanvasRef] = useState(null);
  const [painDiagramPdf, setPainDiagramPdf] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [waitingForSubmission, setWaitingForSubmission] = useState(false);
  const [pdfGenerationProgress, setPdfGenerationProgress] = useState(0);
  const [pdfGenerationStatus, setPdfGenerationStatus] = useState('');

  // CRITICAL #1: Shared state for body map + text checklist (WCAG 1.1.1)
  const [inputMode, setInputMode] = useState('visual'); // 'visual' | 'checklist'
  const [painSelections, setPainSelections] = useState({ selectedAreas: [], painNotes: '' });

  const handlePainSelectionsChange = (data) => {
    setPainSelections(data);
    secureSessionStorage.setItem('pain_location_checklist', data).catch(() => {});
  };

  // M-14: Toolbar position toggle
  const [toolbarSide, setToolbarSide] = useState(() => sessionStorage.getItem('toolbar_side') || 'right');
  const [oneHandedMode, setOneHandedMode] = useState(() => sessionStorage.getItem('one_handed_mode') === 'true');

  const handleToolbarSide = (side) => {
    setToolbarSide(side);
    sessionStorage.setItem('toolbar_side', side);
  };

  const handleOneHandedToggle = () => {
    const newValue = !oneHandedMode;
    setOneHandedMode(newValue);
    sessionStorage.setItem('one_handed_mode', String(newValue));
  };

  // HIGH #9: Fibromyalgia tender point preset
  const [showFibroMap, setShowFibroMap] = useState(false);
  const [fibroPoints, setFibroPoints] = useState([]);

  const handleFibroChange = (points) => {
    setFibroPoints(points);
    secureSessionStorage.setItem('fibro_tender_points', points).catch(() => {});
  };

  // Restore painSelections and fibroPoints from async secure storage on mount
  useEffect(() => {
    (async () => {
      try {
        const storedPain = await secureSessionStorage.getItem('pain_location_checklist');
        if (storedPain) setPainSelections(storedPain);
      } catch { /* ignore */ }
      try {
        const storedFibro = await secureSessionStorage.getItem('fibro_tender_points');
        if (storedFibro) setFibroPoints(storedFibro);
      } catch { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    const handlePainDiagramIncomplete = (event) => {
      if (event.data && event.data.type === 'PAIN_DIAGRAM_INCOMPLETE') {
        setWaitingForSubmission(false);
        setIsGeneratingPdf(false);
        setPdfGenerationProgress(0);
        setPdfGenerationStatus('');
        alert(`Your pain diagram is incomplete (missing ${event.data.missingCount} views). Please redraw the pain diagram.`);
      }
    };

    window.addEventListener('message', handlePainDiagramIncomplete);
    return () => {
      window.removeEventListener('message', handlePainDiagramIncomplete);
    };
  }, []);

  useImperativeHandle(ref, () => ({
    showPopup: () => setShowPopup(true),
    hidePopup: () => setShowPopup(false)
  }));

  useEffect(() => {
    const handlePainDiagramMessage = (event) => {
      
      if (event.data && event.data.type === 'PAIN_DIAGRAM_PDF') {
        setPdfGenerationProgress(100);
        setPdfGenerationStatus('PDF generation complete!');
        
        const base64Response = event.data.fileData;
        const base64Data = base64Response.split(',')[1];
        const byteCharacters = atob(base64Data);
        const byteArrays = [];

        for (let i = 0; i < byteCharacters.length; i += 512) {
          const slice = byteCharacters.slice(i, i + 512);
          const byteNumbers = new Array(slice.length);
          for (let j = 0; j < slice.length; j++) {
            byteNumbers[j] = slice.charCodeAt(j);
          }
          const byteArray = new Uint8Array(byteNumbers);
          byteArrays.push(byteArray);
        }

        const blob = new Blob(byteArrays, { type: 'application/pdf' });
        const file = new File([blob], event.data.fileName, { type: 'application/pdf' });
        setPainDiagramPdf(file);

        if (fileInputRef.current) {
          const dataTransfer = new DataTransfer();
          dataTransfer.items.add(file);
          fileInputRef.current.files = dataTransfer.files;
        }
      }
    };

    const handlePdfGenerationProgress = (event) => {
      if (event.data && event.data.type === 'PDF_GENERATION_PROGRESS') {
        const progress = event.data.progress;
        const status = event.data.status;
        setPdfGenerationProgress(progress || 0);
        setPdfGenerationStatus(status || 'Processing...');
      }
    };

    // Handle PDF size warnings
    const handlePdfSizeWarning = (event) => {
      if (event.data && event.data.type === 'PDF_SIZE_WARNING') {
        logger.warn(`PDF size warning: ${event.data.size} MB`);
      }
    };

    const handlePainDiagramEvent = async () => {
      try {
        const pdfData = await secureLocalStorage.getItem('pain_diagram_pdf_data');
        const fileName = localStorage.getItem('pain_diagram_pdf_name');

        if (pdfData) {
          const base64Data = pdfData.split(',')[1];
          const byteCharacters = atob(base64Data);
          const byteArrays = [];

          for (let i = 0; i < byteCharacters.length; i += 512) {
            const slice = byteCharacters.slice(i, i + 512);
            const byteNumbers = new Array(slice.length);
            for (let j = 0; j < slice.length; j++) {
              byteNumbers[j] = slice.charCodeAt(j);
            }
            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
          }

          const blob = new Blob(byteArrays, { type: 'application/pdf' });
          const file = new File([blob], fileName || 'pain-diagram.pdf', { type: 'application/pdf' });
          setPainDiagramPdf(file);

          if (fileInputRef.current) {
            const dataTransfer = new DataTransfer();
            dataTransfer.items.add(file);
            fileInputRef.current.files = dataTransfer.files;
          }
        }
      } catch (error) {
        logger.error('Error processing pain diagram PDF data:', error);
      }
    };

    window.addEventListener('message', handlePainDiagramMessage);
    window.addEventListener('painDiagramPdfReady', handlePainDiagramEvent);
    window.addEventListener('message', handlePdfGenerationProgress);
    window.addEventListener('message', handlePdfSizeWarning);

    return () => {
      window.removeEventListener('message', handlePainDiagramMessage);
      window.removeEventListener('painDiagramPdfReady', handlePainDiagramEvent);
      window.removeEventListener('message', handlePdfGenerationProgress);
      window.removeEventListener('message', handlePdfSizeWarning);
    };
  }, []);

  useEffect(() => {
    if (painDiagramPdf instanceof File && waitingForSubmission) {
      // Clean up any fallback progress simulation
      if (window._pdfProgressCleanup) {
        window._pdfProgressCleanup();
        delete window._pdfProgressCleanup;
      }

      setWaitingForSubmission(false);
      setIsGeneratingPdf(false);
      setPdfGenerationProgress(0);
      setPdfGenerationStatus('');

      logger.log('PDF ready, proceeding with submission');

      // Proceed with submission now that PDF is ready  
      if (onSubmitWithPopup) {
        onSubmitWithPopup(painDiagramPdf);
      }
    }
  }, [painDiagramPdf, waitingForSubmission, onSubmitWithPopup]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollToTop(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleConfirm = async () => {
    setShowPopup(false);

    if (canvasRef?.checkAndShowAlert && !canvasRef.checkAndShowAlert()) return;

    if (painDiagramPdf instanceof File) {
      if (onSubmitWithPopup) {
        onSubmitWithPopup(painDiagramPdf);
      }
      return;
    }

    // Set states for PDF generation
    setIsGeneratingPdf(true);
    setWaitingForSubmission(true);
    setPdfGenerationProgress(0);
    setPdfGenerationStatus('Initializing PDF generation...');

    let progressInterval;
    let fallbackProgress = 0;
    
    const simulateProgress = () => {
      fallbackProgress += 5;
      if (fallbackProgress <= 90) { // Don't go to 100% with simulation
        setPdfGenerationProgress(fallbackProgress);
        setPdfGenerationStatus(`Processing... ${fallbackProgress}%`);
      }
    };

    const startFallback = setTimeout(() => {
      progressInterval = setInterval(simulateProgress, 500); // Update every 500ms
    }, 2000);

    window._pdfProgressCleanup = () => {
      clearTimeout(startFallback);
      clearInterval(progressInterval);
    };

    // Start PDF generation
    if (canvasRef?.download) {
      canvasRef.download();
    } else {
      logger.error('Canvas download method not available');
      setPdfGenerationStatus('Error: Unable to start PDF generation');
    }
  };

  return (
    <div className={`pain-diagram-container ${toolbarSide === 'left' ? 'toolbar-left' : ''} ${oneHandedMode ? 'one-handed-mode' : ''}`}>
      {/* WCAG 1.1.1: sr-only instruction for screen readers */}
      <p className="sr-only" role="note">
        Choose either the visual body map or the text checklist below.
        Selections in either view will be saved.
      </p>

      {/* M-14: Toolbar position + one-handed mode toggles */}
      <div className="toolbar-position-toggle">
        <button type="button" onClick={() => handleToolbarSide('left')} className={toolbarSide === 'left' ? 'active' : ''}>
          Tools: Left
        </button>
        <button type="button" onClick={() => handleToolbarSide('right')} className={toolbarSide === 'right' ? 'active' : ''}>
          Tools: Right
        </button>
        <button type="button" onClick={handleOneHandedToggle} className={oneHandedMode ? 'active' : ''}>
          {oneHandedMode ? 'Large buttons: ON' : 'Large buttons'}
        </button>
      </div>

      {/* Input mode toggle: Visual Map | Text Checklist */}
      <div className="pain-input-toggle" role="tablist" aria-label="Pain input method">
        <button
          role="tab"
          aria-selected={inputMode === 'visual'}
          aria-controls="pain-visual-panel"
          className={`pain-toggle-btn ${inputMode === 'visual' ? 'active' : ''}`}
          onClick={() => setInputMode('visual')}
          type="button"
        >
          Visual Map
        </button>
        <button
          role="tab"
          aria-selected={inputMode === 'checklist'}
          aria-controls="pain-checklist-panel"
          className={`pain-toggle-btn ${inputMode === 'checklist' ? 'active' : ''}`}
          onClick={() => setInputMode('checklist')}
          type="button"
        >
          Text Checklist
        </button>
      </div>

      {/* Visual map panel */}
      <div
        id="pain-visual-panel"
        role="tabpanel"
        aria-label="Visual body map"
        hidden={inputMode !== 'visual'}
      >
        <DrawingCanvas
          onCanvasReady={setCanvasRef}
          gender={canvasGender}
          setGender={setCanvasGender}
          previousDrawings={previousDrawings}
        />
      </div>

      {/* Text checklist panel (WCAG 1.1.1 accessible alternative) */}
      <div
        id="pain-checklist-panel"
        role="tabpanel"
        aria-label="Text-based pain location checklist"
        hidden={inputMode !== 'checklist'}
      >
        {/* HIGH #9: Fibromyalgia toggle */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
            <input
              type="checkbox"
              checked={showFibroMap}
              onChange={(e) => setShowFibroMap(e.target.checked)}
              style={{ width: '18px', height: '18px', accentColor: 'var(--color-primary, #088395)' }}
            />
            <span style={{ fontWeight: 500 }}>Fibromyalgia Tender Point Map (18-point assessment)</span>
          </label>
        </div>

        {showFibroMap && (
          <FibromyalgiaTenderPoints
            selectedPoints={fibroPoints}
            onChange={handleFibroChange}
          />
        )}

        <PainLocationChecklist
          selectedAreas={painSelections.selectedAreas}
          painNotes={painSelections.painNotes}
          onChange={handlePainSelectionsChange}
        />
      </div>

      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept="application/pdf"
        id="pain_diagram_attachment"
      />

      {painDiagramPdf && (
        <div className="attachement-container">
          <strong>✓ Your Pain Diagram has been successfully attached:</strong>
          <p>
            Thank you for taking the time to complete your pain diagram. This visual
            information will help our medical team better understand your symptoms
            and provide more effective treatment.
          </p>
        </div>
      )}

      {showPopup && (
        <div className="modal-overlay">
          <div className="modal-box">
            <p className="modal-text">
              Ready to submit your pain diagram?
              <br />
              <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 400 }}>
                Please ensure you've marked all areas where you experience pain.
              </span>
            </p>
            <div className="modal-buttons">
              <button
                className="btn-no all-btn"
                onClick={() => setShowPopup(false)}
                disabled={isGeneratingPdf}
              >
                Go Back
              </button>
              <button
                className="btn-yes all-btn"
                onClick={handleConfirm}
                disabled={isGeneratingPdf}
              >
                {isGeneratingPdf ? 'Processing...' : 'Submit Form'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isGeneratingPdf && (
        <div className="modal-overlay">
          <div className="modal-box" style={{ paddingTop: '32px' }}>
            {/* Animated spinner icon */}
            <div style={{
              width: '64px',
              height: '64px',
              margin: '0 auto 16px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #e6f3fa 0%, #e8f5e6 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                border: '3px solid #e2e8f0',
                borderTopColor: 'var(--color-primary, #1a7fc1)',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            </div>
            <p className="modal-text" style={{ padding: '16px 32px 8px' }}>
              Preparing your submission...
            </p>
            <p style={{ fontSize: '14px', color: '#64748b', padding: '0 32px 24px', margin: 0 }}>
              Your form will be submitted automatically once ready.
            </p>

            {/* Progress Bar */}
            <div style={{ padding: '0 32px 32px' }}>
              <div style={{
                width: '100%',
                backgroundColor: '#e2e8f0',
                borderRadius: '12px',
                overflow: 'hidden',
                height: '8px'
              }}>
                <div
                  style={{
                    width: `${Math.max(0, Math.min(100, pdfGenerationProgress))}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, var(--color-primary, #1a7fc1), var(--color-secondary, #5bb450))',
                    transition: 'width 0.5s ease-in-out',
                    borderRadius: '12px'
                  }}
                />
              </div>
              {pdfGenerationStatus && (
                <p style={{
                  fontSize: '13px',
                  color: 'var(--color-primary, #1a7fc1)',
                  textAlign: 'center',
                  margin: '12px 0 0',
                  fontWeight: 500
                }}>
                  {pdfGenerationProgress}% complete
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <div
        style={{
          position: 'fixed',
          left: `${window.innerWidth - 80}px`,
          top: `${window.innerHeight / 5 - 2}px`,
          width: '50px',
          height: '50px',
          borderRadius: '50%',
          backgroundColor: '#3b82f6',
          color: 'white',
          boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          cursor: 'grab',
          zIndex: 1000
        }}
        className="scroll-container"
      >
        {showScrollToTop ? (
          <div onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} title="Scroll Top" className="bounce-up">
            <ChevronUp size={30} />
          </div>
        ) : (
          <div onClick={() => window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' })} title="Scroll Bottom" className="bounce-down" style={{ paddingTop: '8px' }}>
            <ChevronDown size={30} />
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
});

export default Step5Diagram;